/**
 * Post-sign-in return path.
 *
 * Signing in leaves the SPA (Microsoft OAuth round trip) and then walks the
 * MFA challenge, first-time enrolment and PIN setup surfaces. Router state
 * does not survive any of that, so the page the user originally asked for is
 * kept in sessionStorage and restored once the user is fully in.
 *
 * Security: only a same-origin, root-relative path is ever stored or
 * returned. Anything that a browser could resolve to another origin
 * ("//evil.example", "/\\evil.example", "https://…", "javascript:…") is
 * rejected, so this can never become an open redirect.
 */

const STORAGE_KEY = 'hyperion.auth.returnTo';

/** Auth surfaces themselves are never a destination — they would loop. */
const AUTH_SURFACE_PREFIXES = [
  '/login',
  '/first-time',
  '/recovery',
  '/suspended',
  '/mfa/pin-setup',
  '/auth/',
];

export function isSafeReturnPath(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (value.length === 0 || value.length > 2048) return false;
  // Must be root-relative: a single leading slash, never "//" (protocol-
  // relative) and never a backslash (browsers normalise "\" to "/").
  if (value[0] !== '/' || value[1] === '/' || value.includes('\\')) return false;
  // No control characters or whitespace that URL parsing strips/normalises.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\s]/.test(value)) return false;
  // Belt and braces: it must resolve to the current origin.
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const resolved = new URL(value, base);
    if (resolved.origin !== new URL(base).origin) return false;
  } catch {
    return false;
  }
  return true;
}

function isAuthSurface(path: string): boolean {
  const pathname = path.split(/[?#]/, 1)[0];
  if (pathname === '/') return false;
  return AUTH_SURFACE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`)
  );
}

/** A destination worth returning to: safe and not an auth surface or bare root. */
export function isUsefulReturnPath(value: unknown): value is string {
  return isSafeReturnPath(value) && value !== '/' && !isAuthSurface(value);
}

/** Build "pathname + search + hash" from a router/browser location. */
export function locationToPath(loc: { pathname: string; search?: string; hash?: string }): string {
  return `${loc.pathname}${loc.search ?? ''}${loc.hash ?? ''}`;
}

/** Store the destination for after sign-in. Unsafe or pointless paths are ignored. */
export function rememberReturnTo(path: unknown): void {
  if (!isUsefulReturnPath(path)) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, path);
  } catch {
    /* storage unavailable — fall back to the default landing page */
  }
}

/** Read the stored destination without clearing it. */
export function peekReturnTo(): string | null {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    return isUsefulReturnPath(value) ? value : null;
  } catch {
    return null;
  }
}

/** Read and clear the stored destination, falling back when none is valid. */
export function consumeReturnTo(fallback = '/'): string {
  const value = peekReturnTo();
  clearReturnTo();
  return value ?? fallback;
}

export function clearReturnTo(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
